import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactJson from 'react-json-view';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import { NetworkManager } from './NetworkManager';
import { CommonUtil } from '../utils/CommonUtil';

interface JSONCompareViewerProps {
    data: any;
    compareData?: any[]; // Optional array of data to compare against
    onDataChange?: (newData: any) => void;
    title?: string;
    editable?: boolean;
    allowAdd?: boolean;
    allowDelete?: boolean;
    allowCopy?: boolean;
    documentId?: string | number;
    documentType?: 'invoice' | 'statement' | 'payment';
    allowFullscreen?: boolean;
    height?: string;
}

const EMPTY_COMPARE_DATA: any[] = [];

const JSONCompareViewer: React.FC<JSONCompareViewerProps> = ({
    data,
    compareData = EMPTY_COMPARE_DATA,
    onDataChange,
    title = "JSON Viewer",
    editable = true,
    allowAdd = true,
    allowDelete = true,
    allowCopy = true,
    documentId,
    documentType,
    allowFullscreen = true,
    height = "500px"
}) => {
    const [currentData, setCurrentData] = useState(data);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isPdfLoading, setIsPdfLoading] = useState(false);

    useEffect(() => {
        setCurrentData(data);
    }, [data]);

    // Cleanup PDF URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    // Automatically load PDF when document changes
    useEffect(() => {
        setPdfUrl(null); // Clear previous PDF when document changes
        
        if (documentId && documentType !== 'payment') {
            const loadOnInit = async () => {
                setIsPdfLoading(true);
                const headers = new Headers();
                const token = localStorage.getItem("token");
                if (token) headers.append("Authorization", `Bearer ${token}`);

                const url = documentType === "statement"
                    ? `api/download/vHubStatement?statementId=${documentId}`
                    : `api/download/vHubInvoice?invoiceId=${documentId}`;

                try {
                    const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                        method: "GET",
                        headers,
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        if (blob.size > 500) {
                            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                            const blobUrl = window.URL.createObjectURL(pdfBlob);
                            setPdfUrl(blobUrl);
                        }
                    }
                } catch (error) {
                    console.error("Auto PDF load failed:", error);
                } finally {
                    setIsPdfLoading(false);
                }
            };
            loadOnInit();
        }
    }, [documentId, documentType]);

    const downloadPDF = useCallback(async () => {
        if (!documentId) return;

        const orgId = CommonUtil.getSelectedOrgId();
        if (!orgId) return;

        const headers = new Headers({ "Content-Type": "application/json" });
        const token = localStorage.getItem("token");
        if (token) headers.append("Authorization", `Bearer ${token}`);

        const url = documentType === "statement"
            ? `api/download/vHubStatement?statementId=${documentId}`
            : `api/download/vHubInvoice?invoiceId=${documentId}`;

        try {
            const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                method: "GET",
                headers,
            });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${title || 'document'}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            console.error("PDF download failed:", error);
        }
    }, [documentId, documentType, title]);

    const togglePDF = useCallback(async () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
            return;
        }

        if (!documentId) return;

        setIsPdfLoading(true);
        const headers = new Headers();
        const token = localStorage.getItem("token");
        if (token) headers.append("Authorization", `Bearer ${token}`);

        const url = documentType === "statement"
            ? `api/download/vHubStatement?statementId=${documentId}`
            : `api/download/vHubInvoice?invoiceId=${documentId}`;

        try {
            const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            
            if (blob.size < 500) { // Tiny blobs are usually error messages
                const text = await blob.text();
                if (text.includes("errorMessage") || text.includes("error")) {
                    throw new Error(text);
                }
            }

            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(pdfBlob);
            setPdfUrl(blobUrl);
        } catch (error) {
            console.error("PDF preview failed:", error);
            setPdfUrl(null);
            alert("Unable to load PDF preview. Please check if the document exists.");
        } finally {
            setIsPdfLoading(false);
        }
    }, [documentId, documentType, pdfUrl]);

    // Enhanced diff finder with color coding support
    const findMismatches = (obj1: any, obj2: any, path: string = ''): Array<{path: string, value1: any, value2: any}> => {
        let diffs: Array<{path: string, value1: any, value2: any}> = [];
        const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

        keys.forEach(key => {
            const currentPath = path ? `${path}.${key}` : key;
            const val1 = obj1 ? obj1[key] : undefined;
            const val2 = obj2 ? obj2[key] : undefined;

            if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
                diffs = [...diffs, ...findMismatches(val1, val2, currentPath)];
            } else if (val1 !== val2) {
                diffs.push({ path: currentPath, value1: val1, value2: val2 });
            }
        });

        return diffs;
    };

    const mismatches = useMemo(() => {
        if (compareData && compareData.length > 0) {
            return findMismatches(currentData, compareData[0]);
        }
        return [];
    }, [currentData, compareData]);

    const handleEdit = (edit: any) => {
        setCurrentData(edit.updated_src);
        if (onDataChange) {
            onDataChange(edit.updated_src);
        }
    };

    const handleAdd = (add: any) => {
        setCurrentData(add.updated_src);
        if (onDataChange) {
            onDataChange(add.updated_src);
        }
    };

    const handleDelete = (del: any) => {
        setCurrentData(del.updated_src);
        if (onDataChange) {
            onDataChange(del.updated_src);
        }
    };

    // Custom theme with diff highlighting
    const customTheme = {
        scheme: 'monokai',
        author: 'wimer hazenberg (http://www.monokai.nl)',
        base00: '#272822',
        base01: '#383830',
        base02: '#49483e',
        base03: '#75715e',
        base04: '#a59f85',
        base05: '#f8f8f2',
        base06: '#f5f4f1',
        base07: '#f9f8f5',
        base08: '#f92672',
        base09: '#fd971f',
        base0A: '#f4bf75',
        base0B: '#a6e22e',
        base0C: '#a1efe4',
        base0D: '#66d9ef',
        base0E: '#ae81ff',
        base0F: '#cc6633'
    };

    const renderViewer = (src: any, name: string, isEditable: boolean) => (
        <div className="json-viewer-pane" style={{ height: '100%', overflow: 'auto' }}>
            <ReactJson
                src={src}
                theme={customTheme}
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={true}
                enableClipboard={allowCopy}
                onEdit={isEditable ? handleEdit : false}
                onAdd={isEditable && allowAdd ? handleAdd : false}
                onDelete={isEditable && allowDelete ? handleDelete : false}
                name={false}
                style={{ borderRadius: '4px', padding: '10px' }}
                collapsed={false}
                indentWidth={2}
                quotesOnKeys={false}
            />
        </div>
    );

    return (
        <div className={`json-compare-viewer ${isFullscreen ? 'fullscreen-viewer' : ''}`} 
            style={isFullscreen ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                backgroundColor: 'white',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column'
            } : { 
                height: height, 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <style>
                {`
                    .json-compare-viewer .splitter-layout {
                        position: relative;
                        flex: 1;
                    }
                    .json-compare-viewer .splitter-layout .layout-pane {
                        overflow: auto;
                    }
                `}
            </style>
            
            <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold">{title} {isFullscreen ? '(Fullscreen)' : ''}</span>
                    {documentId && documentType !== 'payment' && (
                        <>
                            {isPdfLoading ? (
                                <i className="fa-solid fa-spinner fa-spin text-primary ms-1"></i>
                            ) : (
                                <i 
                                    className={`fa-solid ${pdfUrl ? 'fa-file-pdf text-danger' : 'fa-file-lines text-secondary'} cursor-pointer ms-1`} 
                                    style={{ cursor: 'pointer' }}
                                    onClick={togglePDF}
                                    title={pdfUrl ? "Hide PDF Preview" : "Show PDF Preview"}
                                ></i>
                            )}
                            <i 
                                className="fa-solid fa-download text-primary cursor-pointer ms-2" 
                                style={{ cursor: 'pointer' }}
                                onClick={downloadPDF}
                                title="Download PDF"
                            ></i>
                        </>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2">
                    {compareData.length > 0 && (
                        <span className="badge bg-info text-dark me-2">
                            {mismatches.length} differences found
                        </span>
                    )}
                    {allowFullscreen && (
                        <button 
                            className="btn btn-sm btn-outline-secondary" 
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={isFullscreen ? "Exit Fullscreen" : "Expand Outside"}
                        >
                            <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                        </button>
                    )}
                </div>
            </div>

            {mismatches.length > 0 && (
                <div className="alert alert-warning p-2 mb-0" style={{ fontSize: '12px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <strong>Differences found ({mismatches.length}):</strong>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                // Scroll to first difference (simplified)
                                const firstDiff = document.querySelector('.json-viewer-pane');
                                if (firstDiff) {
                                    firstDiff.scrollTop = 0;
                                }
                            }}
                        >
                            Jump to first
                        </button>
                    </div>
                    <ul className="mb-0 mt-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {mismatches.slice(0, 10).map((m, i) => (
                            <li key={i}>
                                <span className="text-danger">Old:</span> {m.value2} 
                                <span className="text-success ms-2">New:</span> {m.value1}
                                <span className="text-muted ms-2">at <code>{m.path}</code></span>
                            </li>
                        ))}
                        {mismatches.length > 10 && <li>... and {mismatches.length - 10} more</li>}
                    </ul>
                </div>
            )}
            
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {pdfUrl ? (
                    <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={70}>
                        <div style={{ height: '29%', overflowY: 'hidden', position: 'relative' }}>
                            {compareData.length > 0 ? (
                                <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={50}>
                                    {renderViewer(currentData, "New / Edited", editable)}
                                    {renderViewer(compareData[0], "Original", false)}
                                </SplitterLayout>
                            ) : (
                                renderViewer(currentData, title, editable)
                            )}
                        </div>
                        <div className="pdf-viewer-container" style={{ height: '71%', overflowY: 'hidden', backgroundColor: '#525659' }}>
                            <embed 
                                key={pdfUrl}
                                src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=Fit&zoom=53`} 
                                type="application/pdf"
                                width="100%" 
                                height="100%" 
                                style={{ border: 'none', padding: 1, margin: 0 }} 
                            />
                        </div>
                    </SplitterLayout>
                ) : (
                    compareData.length > 0 ? (
                        <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={50}>
                            {renderViewer(currentData, "New / Edited", editable)}
                            {renderViewer(compareData[0], "Original", false)}
                        </SplitterLayout>
                    ) : (
                        renderViewer(currentData, title, editable)
                    )
                )}
            </div>
        </div>
    );
};

export default JSONCompareViewer;